import { Header, StatsCard } from "@general-backoffice/core";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Col, Container, Row } from "reactstrap";

import useReferrersHierarchyStats from "../../../../../components/adminStats/hooks/useReferrersHierarchyStats";
import ReferrersStatsTableCard from "../../../../../components/adminStats/ReferrersStatsTableCard";

const Affiliates: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.adminStatsPages.affiliates" });
  const navigate = useNavigate();

  const {
    rawReferrersHierarchyStats,
    referrersHierarchyStats = [],
    isLoading: isLoadingReferrersHierarchyStats,
    ...referrersHierarchyProps
  } = useReferrersHierarchyStats({ params: { filters: { referrerType: { $in: ["INFLUENCER", "AMBASSADOR"] } } } });

  // Count influencers and ambassadors from the already-fetched data
  // instead of making separate API calls
  const { influencerCount, ambassadorCount, top5 } = useMemo(() => {
    if (!rawReferrersHierarchyStats) return { influencerCount: 0, ambassadorCount: 0, top5: [] };

    let influencers = 0;
    let ambassadors = 0;

    for (const stat of rawReferrersHierarchyStats) {
      if (stat.referrer?.referrerType === "INFLUENCER") influencers++;
      else if (stat.referrer?.referrerType === "AMBASSADOR") ambassadors++;
    }

    const sorted = [...rawReferrersHierarchyStats]
      .sort((a, b) => (b?.charged?.EUR?.total || 0) - (a?.charged?.EUR?.total || 0))
      .slice(0, 5);

    return { influencerCount: influencers, ambassadorCount: ambassadors, top5: sorted };
  }, [rawReferrersHierarchyStats]);

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12} md={6} lg={4}>
          <StatsCard.Number
            color="primary"
            isDark={true}
            number={influencerCount}
            suffix="influencers"
            title={t("influencers")}
            icon="fas fa-bullhorn"
            description="Affiliates of type influencer"
          />
          <StatsCard.Number
            color="default"
            isDark={true}
            number={ambassadorCount}
            suffix="ambassadors"
            title={t("ambassadors")}
            icon="fas fa-bullhorn"
            description="Affiliates of type ambassador"
          />
          <StatsCard.Number
            color="secondary"
            isDark={true}
            number={(rawReferrersHierarchyStats || []).reduce((r, c) => r + (c?.referralsCount || 0), 0)}
            suffix="referrals"
            title={t("referrals")}
            icon="fas fa-bullhorn"
            description="Total users signed using the referral program"
          />
        </Col>
        <Col xs={12} md={6} lg={8}>
          <ReferrersStatsTableCard
            pageSize={5}
            pagination={null}
            sort={{ "charged.EUR.total": "down" }}
            title={t("top5")}
            referrers={top5}
            onReferrerClick={({ referrer }) => navigate(`/users/${referrer?.id}`)}
            isLoading={isLoadingReferrersHierarchyStats}
            columnsToHide={[
              "referrerType",
              "badges",
              "inactiveBalance",
              "totalProfit",
              "totalLosses",
              "signIn",
              "buttons",
              "hierarchyCharged",
            ]}
          />
        </Col>
      </Header.BreadCrumb>
      <Container className="mt--6" fluid>
        <Row>
          <Col xl={12}>
            <ReferrersStatsTableCard
              {...referrersHierarchyProps}
              title={t("all")}
              referrers={referrersHierarchyStats}
              totalReferrers={rawReferrersHierarchyStats?.length}
              onReferrerClick={({ referrer }) => navigate(`/users/${referrer?.id}`)}
              isLoading={isLoadingReferrersHierarchyStats}
              filtersToShow={["search", "isVIP"]}
              columnsToHide={["number", "hierarchyCharged"]}
            />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default Affiliates;
